import AnimationClip from '../AnimationClip';
import GameObject from '../../../GameObject/GameObject';
import Container from '../../../Prototype/Container';
import Point from '../../../Utility/Point';
import TransformComponent from '../../../Component/Transform';
import { Easing, Tween } from '@tweenjs/tween.js';
import { delay } from '../../../Utility/Task/TaskQueue';
import { AwaitIterators, StartAnimationClip } from '../AnimationHelper';
import ScoreCardEffect from '../../../Prototype/Effect/ScoreCardEffect';
import { RenderSystemEvent } from '../../Render/RenderSystem';
import { BoosterType, PreBoosterType } from '../../../State/Card/BoosterType';
import Game from '../../../Game';
import { getRewardData } from '../../../Utility/RewardHelper';
import { RewardType } from '../../../Utility/Rewards/RewardType';
import { report } from '../../../report';
import { RewardBoostersEffect } from './RewardBoostersEffect';
import { RewardBoostersNodeEffect } from './RewardBoostersNodeEffect';
import { getBoosterData } from 'State/Card/BoosterDataMap';

interface IRewardBoostersData {
    type:
        | BoosterType.JOKER
        | BoosterType.UNDO
        | BoosterType.TNT
        | BoosterType.SHUFFLE
        | BoosterType.BULLS_EYE
        | BoosterType.PLUS_FIVE
        // | BoosterType.PLUS_THREE
        | BoosterType.PLUS_ONE
        | RewardType.FREE_PLAY
        | PreBoosterType.REMOVE_THREE
        | PreBoosterType.JOKER
        | PreBoosterType.CANISTER;
    amount: number;
    target: GameObject;
    animateDown?: boolean;
}

export class RewardBoosters extends AnimationClip<IRewardBoostersData> {
    public *execute(animator: GameObject, meta: IRewardBoostersData): IterableIterator<number> {
        try {
            yield* delay(500);

            const { texture, localizationKey } =
                meta.type === RewardType.FREE_PLAY ? getRewardData(meta.type) : getBoosterData(meta.type);

            const localizedBoosterName = Game.locale.getText(localizationKey);

            animator.scene.emit(RenderSystemEvent.BRING_TO_FRONT, animator);

            const layoutContainer: Container = GameObject.Instantiate(Container, {
                name: 'layoutContainer',
                relativePosition: new Point(0.5, 0.5),
            });
            animator.addChild(layoutContainer);

            // Create and show the booster sprite.
            const booster: GameObject = GameObject.Instantiate(Container, {
                anchor: new Point(0.5, 0.5),
                scale: new Point(0.25, 0.25),
            });
            layoutContainer.addChild(booster);
            StartAnimationClip(
                new RewardBoostersEffect(booster, {
                    texture,
                })
            );

            yield* this.showBooster(booster, 500);

            // Create an empty container to get the node position.
            const fakeNode: GameObject = GameObject.Instantiate(Container);
            meta.target.addChild(fakeNode);
            layoutContainer.addChild(fakeNode);
            const nodeTransform = fakeNode.getComponent(TransformComponent);

            StartAnimationClip(new RewardBoostersNodeEffect(fakeNode));
            // Mode booster to the node position.
            yield* this.moveBoosterToLevelNode(booster, nodeTransform, 500);

            // Pop in a text from the node with the booster name.
            layoutContainer.addChild(
                GameObject.Instantiate(ScoreCardEffect, {
                    text: `+${meta.amount} ${localizedBoosterName.replace('\n', ' ')}`,
                    position: nodeTransform.position.clone(),
                    animateDown: meta.animateDown,
                })
            );
            yield* delay(1000);
            fakeNode.destroy();
            layoutContainer.destroy();
        } catch (error) {
            report('RewardBoosters failed', { error, once: true });
        }
    }

    private *showBooster(booster: GameObject, duration: number) {
        const boosterTransform: TransformComponent = booster.getComponent(TransformComponent);
        const tweenScale: Tween = this.getTween(boosterTransform.scale);

        tweenScale.to({ x: 1.2, y: 1.2 }, duration).easing(Easing.Cubic.Out);

        yield* this.startTween(tweenScale);
    }

    private *moveBoosterToLevelNode(booster: GameObject, nodeTransform: TransformComponent, duration: number) {
        const boosterTransform: TransformComponent = booster.getComponent(TransformComponent);

        const tweenPosition: Tween = this.getTween(boosterTransform.position);
        const tweenScale: Tween = this.getTween(boosterTransform.scale);

        tweenPosition.to({ x: nodeTransform.x, y: nodeTransform.y }, duration).easing(Easing.Cubic.In);

        tweenScale
            .to({ x: 0.4, y: 0.4 }, duration)
            .easing(Easing.Cubic.In)
            .onComplete(() => booster.destroy());

        yield* AwaitIterators([this.startTween(tweenPosition), this.startTween(tweenScale)]);
    }
}
